import { useRef } from "react";

import { SWReactIcons } from "@similarweb/icons";

import {
    CREATE_TOPICS_ITEM_ID,
    CREATE_TOPIC_FROM_KW_ITEM_ID,
    TOPIC_HEADER_ID,
} from "app/pages/activation/components/Topics/Dropdown/config";
import { TopicsDropdown } from "app/pages/activation/components/Topics/Dropdown/Dropdown";
import { useTopicsContext } from "app/pages/activation/context/Topics/TopicsContext";
import { useTopicsDataContext } from "app/pages/activation/context/Topics/TopicsDataContext";
import { useTopicsModalContext } from "app/pages/activation/context/Topics/TopicsModalContext";
import { useCreateFlows } from "app/pages/activation/hooks/topics/modal/useCreateFlows";
import { useHandleSuccessCreation } from "app/pages/activation/hooks/topics/modal/useHandleSuccessCreation";
import { activationTracking } from "app/pages/activation/services/tracking";
import { translate } from "app/filters/i18nFilter";
import * as SCHome from "app/pages/activation/modules/Project/styles";

import Duration from "../../Shared/Duration/Duration";
import { headerKeys } from "../automationKeys";
import { MAXIMUM_TOPICS } from "../constants";
import KeywordsDropdown from "../KeywordsDropdown/KeywordsDropdown";
import { CREATED_NUM_TEXT, TOPICS_SUBTITLE, TOPICS_TITLE } from "../texts";

import * as SC from "./styles";

type TopicsHeaderSchema = {
    formattedDuration: string;
    isViewerMode: boolean;
};

export const Header = ({ formattedDuration, isViewerMode }: TopicsHeaderSchema) => {
    const { createdTopicsLength, isMaxCreated, isSuggestion, isLoading, keywordsGroups } = useTopicsDataContext();
    const track = activationTracking.home;

    const { onSort, activationId, granularity } = useTopicsContext();

    const { onCreateTopic } = useTopicsModalContext();

    const { onClose } = useTopicsModalContext();

    const dropdownRef = useRef(null);
    const dropdownKWRef = useRef(null);

    const handleSuccessCreation = useHandleSuccessCreation({ onClose, onSort });

    const { isLoading: isTopicsLoading, onCreateFromKW } = useCreateFlows({
        activationId,
        granularity,
        track,
        handleSuccessCreation,
    });

    const onAddTopicsFromKW = async (keywords: string[]) => {
        await onCreateFromKW(keywords);

        dropdownRef.current?.closePopup();
    };

    const onDDClick = (item: { id: string }) => {
        if (item.id === CREATE_TOPICS_ITEM_ID) {
            onCreateTopic();
        }

        if (item.id === CREATE_TOPIC_FROM_KW_ITEM_ID) {
            dropdownKWRef.current?.popupClickRef?.openPopup();
        }
    };

    const shouldShowActionButton = !isViewerMode && !isSuggestion;

    return (
        <SCHome.WidgetHeader
            data-automation={headerKeys.wrapper}
            id={TOPIC_HEADER_ID}
        >
            <SCHome.WidgetTitle>{translate(TOPICS_TITLE)}</SCHome.WidgetTitle>

            {isSuggestion ? (
                <SC.StyledSuggestionSubtitle data-automation={headerKeys.subtitle}>
                    <SWReactIcons
                        iconName="keyword-group"
                        size="xs"
                    />
                    {translate(TOPICS_SUBTITLE)}
                </SC.StyledSuggestionSubtitle>
            ) : (
                <Duration duration={formattedDuration} />
            )}

            {shouldShowActionButton ? (
                <SC.StyledButtonGroup data-automation={headerKeys.btnGroup}>
                    <SC.StyledNumber>
                        {translate(CREATED_NUM_TEXT, {
                            number: createdTopicsLength,
                            max: MAXIMUM_TOPICS,
                        })}
                    </SC.StyledNumber>

                    <TopicsDropdown
                        dropdownRef={dropdownRef}
                        onClick={onDDClick}
                        isMaxCreated={isMaxCreated}
                    />

                    <KeywordsDropdown
                        createdTopicsLength={createdTopicsLength}
                        isTopicsLoading={isTopicsLoading || isLoading}
                        keywordsGroups={keywordsGroups}
                        dropdownRef={dropdownKWRef}
                        onAddTopics={onAddTopicsFromKW}
                    />
                </SC.StyledButtonGroup>
            ) : null}
        </SCHome.WidgetHeader>
    );
};

Header.displayName = "TopicsHeader";
